// src/components/RespondFeedbackDialog.tsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Chip,
  Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { respondToFeedback } from '../services/feedbackService';
import { Feedback } from '../types/feedback';

interface RespondFeedbackDialogProps {
  open: boolean;
  onClose: () => void;
  feedback: Feedback;
  onSuccess: () => void;
}

const RespondFeedbackDialog: React.FC<RespondFeedbackDialogProps> = ({
  open,
  onClose,
  feedback,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [response, setResponse] = useState('');
  const [markResolved, setMarkResolved] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!response.trim()) {
      setError(t('admin.feedbacks.response') + ' is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await respondToFeedback(feedback.id, {
        admin_response: response.trim(),
        is_resolved: markResolved,
      });
      onSuccess();
    } catch (err: any) {
      console.error('Failed to respond to feedback:', err);
      setError(err.response?.data?.detail || t('admin.feedbacks.errorResponding'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setResponse('');
      setMarkResolved(true);
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('admin.feedbacks.respondTitle')}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Feedback Summary */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <Chip
              label={t(`admin.feedbacks.reportTypes.${feedback.report_type}`)}
              size="small"
              color="primary"
            />
          </Stack>
          <Typography variant="subtitle1" fontWeight="bold">{feedback.title}</Typography>
          <Typography variant="body2" color="text.secondary">{feedback.description}</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            {feedback.user_name} - {new Date(feedback.created_at).toLocaleString()}
          </Typography>
        </Box>

        {/* Response Form */}
        <TextField
          fullWidth
          multiline
          rows={4}
          label={t('admin.feedbacks.response')}
          value={response}
          onChange={(e) => setResponse(e.target.value)}
          disabled={loading}
          placeholder="Enter your response to the user..."
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={markResolved}
              onChange={(e) => setMarkResolved(e.target.checked)}
              disabled={loading}
            />
          }
          label={t('admin.feedbacks.markResolved')}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !response.trim()}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? t('common.loading') : t('admin.feedbacks.respond')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RespondFeedbackDialog;
